import { call, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { lionActions, lionSelectors } from '@rosetta/react-lion';
import Cookies from 'universal-cookie';
import { parse } from 'query-string';
import {
  AUTH_INIT_COMPLETE,
  LOGIN_STATUS,
  ACCOUNT_TYPE,
  LOGOUT_COMPLETE,
  SET_AUTH_ERROR,
  SET_LOGIN_STATUS,
  USER_UPDATE_COMPLETE,
  authActions,
  authSelectors,
  saveLoginCookie,
} from '@rosetta/react-auth';

import {
  INIT_APP,
  LOG_LEVEL,
  LOGIN_USER,
  CONSOLIDATED_LOGIN_USER,
  LOGOUT_USER,
  appActions,
  appSelectors,
  LOG_EVENT,
  SEND_NPS,
  SET_APP_INITIALIZED,
  SET_INTERFACE_LANGUAGE,
  SET_PREFERENCES,
  SET_TARGET_LANGUAGE,
  TRANSACTION_STATE
} from '../reducer/app-reducer';
import {
  emitEvent,
  setUserProperties,
  NPS_SUBMITTED,
  SIGNED_IN,
  SIGNED_OUT,
  SIGN_IN_ERROR_OCCURRED
} from '../../../helpers/eventingHelper';
import { envConfigs } from '../../../envConfigs';
import { getAvailableInterfaceLanguages } from '../services/language-helpers';
import { ltrDir } from '../constants';
import { push, getLocation } from 'connected-react-router';
import { saveTargetLanguage } from '../../launchpad/services/launchpad-api';
import { launchpadActions } from '../../launchpad/reducer/launchpad-reducer';
import { questionnaireActions } from '../../questionnaire/reducer/questionnaire-reducer';
import {
  initBrazeForApp,
  logBrazeEvent,
  setBrazeUserAttribute,
  KEY_CLIENT_ID
} from '../../../helpers/engagementHelper';
import { validateBrowserSupport } from '../services/browser-checker';
import { initEveClient, setSessionAttributes } from '@rosetta/eve-client';

const cookies = new Cookies();

// NOTE: this is a 401 Unauthorized token does not exist, but we only see this error, because ego has CORS issue
// TBD: fix ego CORS issues
const FATAL_AUTH_ERROR = 'system_error_internal_service_error_(500)';

function* initApp() {
  yield initBrazeForApp();
  yield put(lionActions.setLionAppResourcesLoaded(false));

  // https://ixl-learning.atlassian.net/browse/RSCDTS-1429
  // This is logic to check if a consumer user is logged in when they open a new tab
  // A cookie is set in react-auth when they login without remember me, rest is handled here.
  const cookie = cookies.get('RSID') || null;
  const reauth = parse(window.location.search).reauth || null;
  // If we have a consumer user that does not have `remember me` cookie, it's a community session user/cookie,
  // send them back to totale (ie, maybe they opened login page in a new tab while using totale)...  
  if (cookie && typeof cookie !== 'string' && cookie.account_type === ACCOUNT_TYPE.CONSUMER && !cookie.rm_key && !cookie.rm_guid) {
    if (reauth) {
      // ...but if we have `reauth=true` in url, then community is telling us their session expired.
      // delete this session cookie so they can login again
      try {
        cookies.remove('RSID')
      } catch (e) {
        console.warn('Error removing cookie:')
        console.log(e)
      }
    } else {
      return window.location.href = envConfigs.totaleApiHost;
    }
  }

  yield put(
    authActions.initializeAuth({
      appId: 'launchpad',
      tullyHost: envConfigs.tullyApiHost,
      lpsHost: envConfigs.lpsApiHost,
      totaleHost: envConfigs.totaleApiHost,
      sirenHost: envConfigs.sirenApiHost,
      egoHost: envConfigs.egoApiHost
    })
  );
  // wait until all the user-checking is complete
  yield take(AUTH_INIT_COMPLETE);

  yield initLion();
  yield installInterfaceLanguage();

  // Indicate successful load to the engagement service.
  logBrazeEvent('LaunchPadLoaded', {});

  if (!validateBrowserSupport()) {
    yield put(appActions.setUnsupportedBrowser(true));
  }
  yield put(appActions.setAppInitialized());
}

// this tells lion where to find the localizations
function* makeLionResourcesPath(langCode) {
  const localizationsPath = yield select(appSelectors.localizationsPath);
  return localizationsPath + 'language-' + langCode + '.json';
}

function* initLion() {
  yield put(lionActions.setLionAppResourcesLoaded(false));
  const localizationsPath = '/assets/lion/';
  yield put(appActions.setLocalizationsPath(localizationsPath));

  const fallbackLocale = 'en-US';
  yield put(lionActions.setLionLanguage(fallbackLocale));
  yield put(lionActions.setLionFallbackLanguage(fallbackLocale));
  const fallbackLocalizationsFilePath = yield call(makeLionResourcesPath, fallbackLocale);
  const fallbackAppResources = yield fetch(fallbackLocalizationsFilePath).then((response) => response.json());
  yield put(lionActions.setLionAppResources(fallbackAppResources, fallbackLocale));
  yield put(appActions.setInterfaceLanguageDirection(ltrDir));
}

function* installInterfaceLanguage(userInterfaceLanguageCode) {
  yield put(lionActions.setLionAppResourcesLoaded(false));
  if (!userInterfaceLanguageCode) {
    userInterfaceLanguageCode = yield select(authSelectors.userInterfaceLanguage);
  }
  if (!userInterfaceLanguageCode) {
    // if no user then use the fallback language
    // get from the totale cookie, if it exists
    const totale_locale = cookies.get('locale');
    if (totale_locale) {
      userInterfaceLanguageCode = totale_locale;
    } else {
      userInterfaceLanguageCode = yield select(lionSelectors.getLionFallbackLanguage);
    }
  }
  // is this user's interface language an available one?
  const availLanguages = yield call(getAvailableInterfaceLanguages);
  const userInterfaceLanguage = availLanguages.find((aLangObj) => {
    return aLangObj.value === userInterfaceLanguageCode;
  });
  if (!userInterfaceLanguage) {
    // if user language if invalid then log it and use the fallback language
    yield put(
      appActions.logEvent({
        level: LOG_LEVEL.INFO,
        message: 'interface language ' + userInterfaceLanguageCode + ' not available'
      })
    );
    userInterfaceLanguageCode = yield select(lionSelectors.getLionFallbackLanguage);
  }

  const currentInterfaceLanguageCode = yield select(lionSelectors.getLionLanguage);
  if (currentInterfaceLanguageCode === userInterfaceLanguageCode) {
    // this language already set, no more to do here
    return yield put(lionActions.setLionAppResourcesLoaded(true));
  }

  yield put(lionActions.setLionLanguage(userInterfaceLanguageCode));
  const lionFilePath = yield call(makeLionResourcesPath, userInterfaceLanguageCode);
  const lionResources = yield fetch(lionFilePath).then((response) => response.json());
  yield put(lionActions.setLionAppResources(lionResources, userInterfaceLanguageCode));
  yield put(appActions.setInterfaceLanguageDirection(userInterfaceLanguage.dir));
  yield put(lionActions.setLionAppResourcesLoaded(true));
  setSessionAttributes({ locale: userInterfaceLanguageCode });
}

function initEve(user, authToken) {
  if (authToken) {
    initEveClient(
      envConfigs.eveHost,
      'tully:' + authToken,
      user.gui,
      user.organization,
      envConfigs.appName,
      'n/a',
      true, // Start polling
      true // instrumentErrors
    );
  }
}

function* loginUser(action) {
  // LOGIN_USER payload has two flavors:
  // 1) if token provided, use that to get token data and login as that user
  // 2) if no token, then use username & password to attempt login
  yield put(authActions.loginUser(action.payload.username, action.payload.password, action.payload.token));
  let loginStatus;
  while (true) {
    yield take(SET_LOGIN_STATUS);
    loginStatus = yield select(authSelectors.loginStatus);
    if (loginStatus !== LOGIN_STATUS.PENDING) {
      break;
    }
  }
  if (loginStatus === LOGIN_STATUS.LOGGED_IN) {
    yield installInterfaceLanguage();
    const user = yield select(authSelectors.userInfo);
    const clientId = yield select(authSelectors.clientId);
    const authToken = yield select(authSelectors.authToken);
    yield initEve(user, authToken);
    setBrazeUserAttribute(KEY_CLIENT_ID, clientId);
    yield call(setUserProperties, user);
    yield call(emitEvent, SIGNED_IN, { 'Sign In Method': 'E&E' });
    yield put(push('/launchpad'));
  } else {
    const user = { email: action.payload.username };
    yield call(setUserProperties, user);
    yield call(emitEvent, SIGN_IN_ERROR_OCCURRED, {
      'Sign In Method': 'E&E',
      Email: action.payload.username,
      Detail: loginStatus
    });
  }
}

function* consolidatedLoginUser(action) {
  const {username, password, namespace, accountType, rememberMeChecked, rememberMeCookie} = {...action.payload}
  // for consolidated login, need to handle three types of users: Consumer, Catalyst, Standalone Foundations
  yield put(authActions.consolidatedLoginUser(username, password, namespace, accountType, rememberMeChecked, rememberMeCookie));

  let loginStatus;
  while (true) {
    yield take(SET_LOGIN_STATUS);
    loginStatus = yield select(authSelectors.loginStatus);
    if (loginStatus !== LOGIN_STATUS.PENDING) {
      break;
    }
  }

  if (loginStatus === LOGIN_STATUS.LOGGED_IN) {
    let loggedInAccountType = yield select(authSelectors.accountType);
    if (loggedInAccountType === ACCOUNT_TYPE.CATALYST) {
      yield installInterfaceLanguage();
      const user = yield select(authSelectors.userInfo);
      const clientId = yield select(authSelectors.clientId);
      const authToken = yield select(authSelectors.authToken);
      yield initEve(user, authToken);
      setBrazeUserAttribute(KEY_CLIENT_ID, clientId);
      yield call(setUserProperties, user);
      yield call(emitEvent, SIGNED_IN, { 'Sign In Method': 'E&E' });
      yield put(push('/launchpad'));
    } else if (loggedInAccountType === ACCOUNT_TYPE.CONSUMER) {
      // noting to do now
    } else if (loggedInAccountType === ACCOUNT_TYPE.STANDALONE) {
      // noting to do now
    }

  } else {
    const user = { email: action.payload.username };
    yield call(setUserProperties, user);
    yield call(emitEvent, SIGN_IN_ERROR_OCCURRED, {
      'Sign In Method': 'E&E',
      Email: action.payload.username,
      Detail: loginStatus
    });
  }
}

function* logoutUser() {
  const isInitialized = yield select(appSelectors.isAppInitialized);
  if (!isInitialized) {
    yield take(SET_APP_INITIALIZED);
  }
  yield put(authActions.logout());
}

function* logoutComplete(action) {
  // the following block is because:
  //  the register page wants to call logout to make sure the decks are clear
  //  but it does  not want to automatically to back to login at that point
  const currentLocation = yield select(getLocation);
  if (currentLocation.pathname.indexOf('register') !== -1) {
    return null;
  }
  // don't go anywhere if this logout happens while the app is initializing
  const isInitialized = yield select(appSelectors.isAppInitialized);
  if (isInitialized) {
    yield put(launchpadActions.clearLaunchpad());
    yield put(questionnaireActions.resetQuestionnaire());
    yield call(emitEvent, SIGNED_OUT);
    if (action.payload.wasSsoUser) {
      yield put(push('/logoutsso'));
    } else {
      yield put(push('/login'));
    }
  }
}

// this is the public call which should handle overlapping requests and user update
function* setInterfaceLanguage(action) {
  let newLangCode = action.payload.languageCode;
  if (!newLangCode) {
    return yield put(
      appActions.logEvent({
        level: LOG_LEVEL.ERROR,
        message: 'attempt to set interface language with falsy languageCode'
      })
    );
  }
  const isInitialized = yield select(appSelectors.isAppInitialized);
  if (!isInitialized) {
    yield take(SET_APP_INITIALIZED);
  }

  yield put(lionActions.setLionAppResourcesLoaded(false));
  const status = yield select(authSelectors.loginStatus);
  const haveUser = status === LOGIN_STATUS.LOGGED_IN;
  if (haveUser) {
    // update the user
    const currentCode = yield select(authSelectors.userInterfaceLanguage);
    if (currentCode !== newLangCode) {
      yield put(authActions.updateUser({ interfaceLanguage: newLangCode }));
      yield take(USER_UPDATE_COMPLETE);
      newLangCode = yield select(authSelectors.userInterfaceLanguage);
      const user = yield select(authSelectors.userInfo);
      user.interfaceLanguage = newLangCode;
      yield call(setUserProperties, user);
    }
  } else {
    // The above authActions.updateUser persists this to the cookie for us
    // Without having a user login, we need to save this for later
    yield call(saveLoginCookie, {interfaceLanguage: newLangCode});
  }
  return yield installInterfaceLanguage(newLangCode);
}

function* setPreferences(action) {
  yield put(push('/preferences'));
}

function* setUserTargetLanguage(action) {
  yield put(appActions.setLastTransactionState(TRANSACTION_STATE.PENDING));
  const isInitialized = yield select(appSelectors.isAppInitialized);
  if (!isInitialized) {
    yield take(SET_APP_INITIALIZED);
  }
  const status = yield select(authSelectors.loginStatus);
  if (status !== LOGIN_STATUS.LOGGED_IN) {
    yield put(
      appActions.logEvent({
        level: LOG_LEVEL.ERROR,
        message: 'attempt to set target language with no user logged in'
      })
    );
    return yield put(appActions.setLastTransactionState(TRANSACTION_STATE.FAIL));
  }

  const newLanguageCode = action.payload.languageCode;
  const currentLanguageCode = yield select(authSelectors.targetLanguageCode);
  if (newLanguageCode !== currentLanguageCode) {
    const userId = yield select(authSelectors.userId);
    const token = yield select(authSelectors.authToken);
    try {
      yield call(saveTargetLanguage, userId, newLanguageCode, token);
      yield put(authActions.refreshUser());
      yield take(AUTH_INIT_COMPLETE);
      yield put(appActions.setLastTransactionState(TRANSACTION_STATE.SUCCESS));
      yield put(launchpadActions.clearLaunchpad());
      return yield put(push('/launchpad'));
    } catch (ex) {
      let errorMsg = 'error setting target language: ' + ex.message;
      yield put(
        appActions.logEvent({
          level: LOG_LEVEL.ERROR,
          message: errorMsg
        })
      );
      return yield put(appActions.setLastTransactionState(TRANSACTION_STATE.FAIL, errorMsg));
    }
  }
}

function* logEvent(action) {
  // we might want to do more here ...
  yield console.log('*** ' + action.payload.level + ' ***  ' + action.payload.message);
}

function* handleAuthError(action) {

  const errorMsg = action.payload.errorMsg;
  
  yield call(logEvent, { payload: { level: LOG_LEVEL.ERROR, message: 'auth error: ' + errorMsg } });

  // During react-auth login, if tully token has expired or been deleted, the first titan call that fails is
  // the call to get userInfo here: https://stash.trstone.com/projects/DNAT/repos/react-auth/browse/src/sagas/auth-sagas.js#111
  // Since react-auth otherwise assumes the token is OK until this call, it is first failure that indicates that our token is invalid
  
  if (errorMsg === FATAL_AUTH_ERROR) {
    window.location.assign('/logout');
  }
}

function* sendNpsValue(action) {
  yield call(emitEvent, NPS_SUBMITTED, { Score: action.payload.score });
}

export function* appSagas() {
  yield takeLatest(INIT_APP, initApp);
  yield takeLatest(SET_INTERFACE_LANGUAGE, setInterfaceLanguage);
  yield takeLatest(LOGIN_USER, loginUser);
  yield takeLatest(CONSOLIDATED_LOGIN_USER, consolidatedLoginUser);
  yield takeLatest(LOGOUT_USER, logoutUser);
  yield takeLatest(LOGOUT_COMPLETE, logoutComplete);
  yield takeLatest(SET_PREFERENCES, setPreferences);
  yield takeLatest(SET_TARGET_LANGUAGE, setUserTargetLanguage);
  // front-end logging
  yield takeEvery(LOG_EVENT, logEvent);
  // for front-end logging we can take all the auth errors and publish them if we want
  yield takeEvery(SET_AUTH_ERROR, handleAuthError);
  yield takeLatest(SEND_NPS, sendNpsValue);
}



// WEBPACK FOOTER //
// ./src/modules/app/sagas/app-sagas.js